import React, { useState } from 'react';
import { Button, Card, Form, Modal, Table } from 'react-bootstrap';

import { useLanguage } from '../../context/LanguageProvider/LanguageProvider';
import { useAdminData } from '../../context/AdminDataProvider/AdminDataProvider';

const emptyDomain = { name: '', slug: '', description: '' };

export default function AdminDomainsPage() {
  const { t } = useLanguage();
  const { domains, addDomain, updateDomain, deleteDomain, domainInUse } = useAdminData();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyDomain);

  const openAdd = () => {
    setEditingId(null);
    setForm(emptyDomain);
    setModalOpen(true);
  };

  const openEdit = (d) => {
    setEditingId(d.id);
    setForm({ name: d.name, slug: d.slug, description: d.description || '' });
    setModalOpen(true);
  };

  const closeModal = () => setModalOpen(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.slug.trim()) return;
    const slug = form.slug.trim().toLowerCase().replace(/\s+/g, '-');
    const payload = { ...form, slug };
    if (editingId) {
      await updateDomain(editingId, payload);
    } else {
      await addDomain(payload);
    }
    closeModal();
  };

  const handleDelete = async (id) => {
    if (domainInUse(id)) {
      window.alert(t('admin.crud.domainInUse'));
      return;
    }
    if (window.confirm(t('admin.crud.confirmDelete'))) {
      await deleteDomain(id);
    }
  };

  return (
    <div className="jassp-admin-crud">
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-4">
        <h1 className="h3 fw-bold text-dark mb-0">{t('admin.crud.domainsTitle')}</h1>
        <Button variant="primary" className="rounded-pill px-4" onClick={openAdd}>
          {t('admin.crud.addDomain')}
        </Button>
      </div>

      <Card className="border-0 shadow-sm">
        <Card.Body className="p-0">
          <Table responsive hover className="mb-0 align-middle">
            <thead className="table-light">
              <tr>
                <th className="ps-4">{t('admin.crud.colName')}</th>
                <th>{t('admin.crud.colSlug')}</th>
                <th>{t('admin.crud.colDescription')}</th>
                <th className="text-end pe-4">{t('admin.crud.colActions')}</th>
              </tr>
            </thead>
            <tbody>
              {domains.map((d) => (
                <tr key={d.id}>
                  <td className="ps-4">{d.name}</td>
                  <td>
                    <code>{d.slug}</code>
                  </td>
                  <td className="text-muted small">{d.description}</td>
                  <td className="text-end pe-4">
                    <Button variant="link" size="sm" className="text-primary p-1" onClick={() => openEdit(d)}>
                      {t('admin.crud.edit')}
                    </Button>
                    <Button variant="link" size="sm" className="text-danger p-1" onClick={() => handleDelete(d.id)}>
                      {t('admin.crud.delete')}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      <Modal show={modalOpen} onHide={closeModal} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{editingId ? t('admin.crud.editDomain') : t('admin.crud.addDomain')}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>{t('admin.crud.colName')}</Form.Label>
              <Form.Control
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>{t('admin.crud.colSlug')}</Form.Label>
              <Form.Control
                value={form.slug}
                onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))}
                placeholder="observatories"
                required
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>{t('admin.crud.colDescription')}</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="light" type="button" onClick={closeModal}>
              {t('admin.crud.cancel')}
            </Button>
            <Button variant="primary" type="submit">
              {t('admin.crud.save')}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
}
